<template>
  <div class="flex items-center space-x-4" ref="audioSelectorRef">
    <div>
      <div class="rounded-full w-12 h-12 flex justify-center items-center relative z-10">
        <ViceButton
          :data-cy="`microphone-toggle-${
            multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn
          }`"
          circle
          grey
          class="disabled:cursor-not-allowed w-12 h-12 transition-opacity hover:bg-gray-100"
          :class="{
            'bg-vice-teal-600 hover:bg-vice-teal-700':
              multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn,
          }"
          @click="toggleUserMicrophone"
          :disabled="
            multiCameraStore.externalAudioIO ||
            isDisabledMediaControls ||
            !multiCameraStore.devicesInitialized
          "
        >
          <MicrophoneOn
            v-if="multiCameraStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM].isMicrophoneOn"
          />
          <MicrophoneMutedIcon v-else />
        </ViceButton>
      </div>
    </div>

    <div
      class="relative inline-block text-left"
      :class="{
        'pointer-events-none':
          multiCameraStore.externalAudioIO ||
          isDisabledMediaControls ||
          !multiCameraStore.devicesInitialized,
      }"
    >
      <div
        @click="dropDownOpen = !dropDownOpen"
        class="
          items-center
          rounded-full
          text-sm
          font-medium
          text-white
          devices-dropdown
          grid
          justify-items-end
          w-20
          h-12
          -ml-16
        "
      >
        <span class="text-xl mr-1 w-6 select-none" :class="{ 'text-vice-dark-grey': dropDownOpen }">
          ···
        </span>
      </div>
    </div>

    <div
      class="absolute z-20 content-center flex items-center w-full bottom-28 left-0 h-0"
      :class="sessionStore.isClinician ? 'pr-7 justify-end' : 'pr-4 justify-center'"
    >
      <!-- Audio Selectors -->
      <div
        v-if="dropDownOpen"
        class="w-1/3 h-14 flex gap-3 items-center dropdown-border py-1 px-2 rounded-full border"
      >
        <div class="inline-flex h-10 w-1/2 border-2 rounded-full border-vice-teal-700 bg-gray-50">
          <label for="mic" class="flex items-center text-sm font-medium text-gray-700 ml-1">
            <ViceMicrophone
              pathFill="#1A3B47"
              lineOneStroke="#1A3B47"
              lineTwoStroke="#1A3B47"
              class="h-7 w-7"
            />
          </label>
          <select
            id="mic"
            class="
              text-xs
              font-semibold
              w-full
              pr-7
              pl-0
              py-0
              border-none border-0
              rounded-full
              focus:outline-none
              focus:border-none
              focus:ring-0
              text-gray-700
              bg-gray-50
            "
            @change="chooseMic($event.target.value)"
          >
            <option
              v-for="(mic, index) in mics"
              :selected="multiCameraStore.selectedMicDeviceId === mic.deviceId"
              :value="mic.deviceId"
              :key="index"
            >
              {{ mic.label }}
            </option>
          </select>
        </div>

        <div class="inline-flex h-10 w-1/2 border-2 rounded-full border-vice-teal-700 bg-gray-50">
          <label for="speaker" class="flex items-center text-sm font-medium text-gray-700 ml-1">
            <ViceSpeakers class="h-5 w-8" pathFill="#1A3B47" />
          </label>
          <select
            id="speaker"
            class="
              text-xs
              font-semibold
              w-full
              pr-7
              pl-0
              py-0
              border-none border-0
              rounded-full
              focus:outline-none
              focus:border-none
              focus:ring-0
              text-gray-700
              bg-gray-50
            "
            @change="chooseSpeaker($event.target.value)"
          >
            <option
              v-for="(speaker, index) in speakers"
              :selected="multiCameraStore.selectedSpeakerDeviceId === speaker.deviceId"
              :value="speaker.deviceId"
              :key="index"
            >
              {{ speaker.label }}
            </option>
          </select>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onClickOutside } from '@vueuse/core';
import { ref, watch, onMounted, computed } from 'vue';
import useDeviceAndSessionSetupStore from '@/Stores/useDeviceAndSessionSetupStore';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import {
  CAMERA_TYPES,
  DEVICE_NAME,
  DEVICES_KINDS,
  MEDIA_TYPE,
  SMART_GLASS_DEVICE_LABEL,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import MicrophoneMutedIcon from '@/Components/Icons/MicrophoneMutedIcon.vue';
import MicrophoneOn from '@/Components/Icons/MicrophoneOn.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import useSessionStore from '@/Stores/useSessionStore';
import ViceMicrophone from '@/Components/Icons/ViceMicrophone.vue';
import ViceSpeakers from '@/Components/Icons/ViceSpeakers.vue';

export default {
  name: 'DefaultAudioDeviceSelector',
  computed: {
    VICE_SESSION_PHASES() {
      return VICE_SESSION_PHASES;
    },
    DEVICE_NAME() {
      return DEVICE_NAME;
    },
    CAMERA_TYPES() {
      return CAMERA_TYPES;
    },
  },
  props: {
    defaultDevices: {
      type: Object,
      required: true,
    },
  },
  components: {
    ViceSpeakers,
    ViceMicrophone,
    MicrophoneOn,
    MicrophoneMutedIcon,
    ViceButton,
  },
  setup() {
    const mics = ref([]);
    const speakers = ref([]);
    const dropDownOpen = ref(false);
    const cameraLoading = ref(false);
    const audioSelectorRef = ref(null);

    const sessionStore = useSessionStore();
    const multiCameraStore = useLocalStreamingDevicesStore();
    const deviceAndSessionSetupStore = useDeviceAndSessionSetupStore();

    const selectedCameraType = computed(() => multiCameraStore.currentActiveCameraType);

    const isDisabledMediaControls = computed(
      () =>
        !multiCameraStore.mediaActivated ||
        sessionStore.isPutOnHold ||
        (sessionStore.isPhasePaused && sessionStore.isClinician) ||
        sessionStore.mediaPermissionError,
    );

    const cameraSelectDisabled = computed(
      () => isDisabledMediaControls.value || cameraLoading.value,
    );

    onMounted(() => {
      const audioDevices = multiCameraStore.groupedDeviceAsAudioAndVideo[MEDIA_TYPE.AUDIO];

      mics.value = audioDevices?.[DEVICE_NAME.MICROPHONE];
      speakers.value = audioDevices?.[DEVICE_NAME.SPEAKERS];
    });

    // Watch for micros
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(DEVICES_KINDS.AUDIO_INPUT),
      (devicesList) => {
        mics.value = devicesList;
      },
    );

    // Watch for a speaker
    watch(
      () => deviceAndSessionSetupStore.getConnectedDevices(DEVICES_KINDS.AUDIO_OUTPUT),
      (devicesList) => {
        speakers.value = devicesList;
      },
    );

    const isActiveDevice = (deviceName) =>
      multiCameraStore.allCameraTypes[selectedCameraType.value].deviceSelection[deviceName].active;

    const chooseMic = (id) => {
      const prop = mics.value.find((mic) => mic.deviceId === id);
      multiCameraStore.chooseMediaSource(selectedCameraType.value, DEVICE_NAME.MICROPHONE, prop);
    };

    const chooseSpeaker = (id) => {
      const prop = speakers.value.find((speaker) => speaker.deviceId === id);
      multiCameraStore.chooseMediaSource(selectedCameraType.value, DEVICE_NAME.SPEAKERS, prop);
    };

    const audioDeviceDisplay = (deviceProps, deviceName) =>
      deviceProps[0]?.kind.startsWith('audio') && multiCameraStore.externalAudioIO
        ? SMART_GLASS_DEVICE_LABEL
        : multiCameraStore.activeDeviceLabels[deviceName];

    // Allow audio selection for just the internal camera so that we have only one source
    const isDisabledAudioSelection = () =>
      selectedCameraType.value !== CAMERA_TYPES.INTERNAL_WEBCAM;

    const toggleUserMicrophone = () => {
      multiCameraStore.toggleMicrophone(CAMERA_TYPES.INTERNAL_WEBCAM);
    };

    const toggleCamera = async () => {
      cameraLoading.value = true;
      await multiCameraStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
      cameraLoading.value = false;
    };

    onClickOutside(audioSelectorRef, () => {
      dropDownOpen.value = false;
    });

    return {
      mics,
      speakers,
      sessionStore,
      dropDownOpen,
      cameraLoading,
      audioSelectorRef,
      multiCameraStore,
      cameraSelectDisabled,
      isDisabledMediaControls,
      chooseMic,
      toggleCamera,
      chooseSpeaker,
      isActiveDevice,
      audioDeviceDisplay,
      toggleUserMicrophone,
      isDisabledAudioSelection,
    };
  },
};
</script>
<style scoped>
.devices-dropdown {
  background-color: #1e1e1e;
  color: #d4d4d4;
}
.dropdown-border {
  border: 2px solid rgb(38 37 37);
  background-color: #1e1e1e;
}
</style>
